import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import Chevron from './Chevron';

export interface FilterDropdownProps {
    children: (open: boolean) => React.ReactNode;
    title: string;
    isFiltered: boolean;
}

const Container = styled.div<{ open: boolean }>`
  border-radius: 8px;
  background-color: white;
  border: 1px solid var(--color-charcoal-v20);
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  max-width: 100%;
  padding: 8px 12px;
  cursor: pointer;
  color: var(--color-charcoal-v100);
  z-index: 1;
  &, * {
    box-sizing: border-box;
  }
  transition-property: background-color, border-color;
  transition-duration: 150ms;

  ${(props) => props.open && css`
    border-color: var(--color-bluescape-v100);
    background-color: var(--color-bluescape-v05);
  `}
`;

const Title = styled.span<{ isFiltered: boolean, open: boolean }>`
  max-height: 1.2em;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  font-size: 18px;
  > :first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 12px;
    max-width: 100%;
  }

  ${(props) => props.isFiltered && css`
    font-weight: 600;
  `}
`;

const Dropdown = styled.div<{ open: boolean }>`
  cursor: auto;
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  width: 100%;
  background-color: white;
  border: inherit;
  border-color: var(--color-charcoal-v20);
  border-radius: inherit;
  box-shadow: inherit;
  font-size: 18px;
  box-shadow: ${(props) => props.theme.shadows.hard};
  padding: 11px 16px;

  transition: opacity 150ms;
  pointer-events: none;
  opacity: 0;

  ${({ open }) => open && css`
    display: block;
    opacity: 1;
    pointer-events: unset;
  `}
`;

const FilterDropdown: React.FC<FilterDropdownProps> = (props) => {
  const [open, setOpen] = React.useState(false);
  const { title } = props;

  useEffect(() => {
    const handleClickOutside = () => setOpen(false);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);
  return (
    <Container
      tabIndex={0}
      role="button"
      aria-expanded={open}
      open={open}
      onClick={(e) => {
        e.stopPropagation();
        setOpen(!open);
      }}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          setOpen(!open);
        }
      }}
    >
      <Title
        open={open}
        isFiltered={props.isFiltered}
      >
        <span>{title}</span>
        <Chevron open={open} />
      </Title>
      <Dropdown onClick={(e) => e.stopPropagation()} open={open}>
        {props.children(open)}
      </Dropdown>
    </Container>
  );
};

export default FilterDropdown;
